import React from 'react';
import { Reveal, SectionEyebrow } from '../components/ui-bits';
import Seo from '../components/Seo';

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="bg-white">
      <Seo
        title="Privacy & Cookies"
        description="How David Ball Management collects, uses and protects your personal data. Our approach to cookies, analytics and contact form enquiries under UK GDPR."
        path="/privacy"
      />
      <section className="pt-40 pb-16 lg:pt-48 lg:pb-24 bg-[#faf8f4]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <Reveal>
            <SectionEyebrow>Legal</SectionEyebrow>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-heading uppercase text-[#002b5c] text-5xl sm:text-6xl md:text-7xl mt-8 leading-[0.9]">
              Privacy <span className="italic font-light">& Cookies</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <article className="max-w-[820px] mx-auto px-6 lg:px-8 py-20 lg:py-28 prose-lg">
        <p className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b] mb-12">
          Last updated · 28 February 2026
        </p>

        <Section title="Who we are">
          <p>
            This website (<a href="https://dbmtalent.com" className="gold-underline">www.dbmtalent.com</a>) is
            operated by David Ball Limited, a company registered in England &amp; Wales. Our registered
            correspondence address is 29 Holmdale Road, London BR7 6BY, United Kingdom.
            For any privacy question you can write to us at{' '}
            <a href="mailto:office@dbmtalent.com" className="gold-underline">office@dbmtalent.com</a>{' '}
            or call <a href="tel:+442033550561" className="gold-underline">+44 (0) 203 355 0561</a>.
          </p>
          <p>
            We are the data controller for personal data collected via this website under
            the UK GDPR and the Data Protection Act 2018.
          </p>
        </Section>

        <Section title="What we collect and why">
          <p>We only collect the information we genuinely need in order to represent our clients and reply to enquiries.</p>
          <ul>
            <li><strong>Contact form:</strong> your name, email, optional phone number, subject and message. We use this only to reply to you and to keep a record of the enquiry.</li>
            <li><strong>Newsletter:</strong> your email address, used solely to send you The Green Room newsletter. Unsubscribe at any time by replying to any newsletter.</li>
            <li><strong>Client relationships:</strong> where you are represented by David Ball Management, we hold the professional information required for casting submissions and career management.</li>
            <li><strong>Analytics (optional, consent-gated):</strong> anonymised usage information via PostHog to understand how the site is used and improve it. This is only loaded if you accept cookies.</li>
          </ul>
        </Section>

        <Section title="Our lawful basis">
          <p>
            We rely on <strong>consent</strong> for the newsletter and any optional analytics cookies, and on{' '}
            <strong>legitimate interests</strong> for replying to enquiries you initiate and running the agency.
          </p>
        </Section>

        <Section title="How we share your information">
          <p>
            We do not sell your personal data. We share information only where necessary, for
            example with casting directors and producers when submitting our clients for work,
            or with trusted service providers who help us operate the website (hosting,
            email delivery and analytics). All processors are bound by written agreements.
          </p>
        </Section>

        <Section title="How long we keep it">
          <p>
            Contact form enquiries are typically kept for up to two years so that we can follow up.
            Newsletter subscriptions are kept until you unsubscribe. Client information is kept
            for the duration of representation and a reasonable period after, in line with our
            tax and regulatory obligations.
          </p>
        </Section>

        <Section title="Cookies">
          <p>
            This site uses only the cookies strictly needed to load the page. Optional analytics
            cookies (PostHog) are set only if you click <em>Accept</em> on the cookie banner.
            You can withdraw consent at any time by clearing your browser storage for
            www.dbmtalent.com — the banner will re-appear on your next visit.
          </p>
          <p>The site does not currently use any advertising or third-party marketing cookies.</p>
        </Section>

        <Section title="Your rights">
          <p>Under the UK GDPR you have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you.</li>
            <li>Ask us to correct anything that is inaccurate.</li>
            <li>Ask us to delete your data where we no longer need it.</li>
            <li>Object to or restrict certain processing.</li>
            <li>Withdraw consent at any time.</li>
            <li>Complain to the UK Information Commissioner's Office (<a href="https://ico.org.uk" className="gold-underline" target="_blank" rel="noopener noreferrer">ico.org.uk</a>).</li>
          </ul>
          <p>
            To exercise any of these rights, email{' '}
            <a href="mailto:office@dbmtalent.com" className="gold-underline">office@dbmtalent.com</a>.
          </p>
        </Section>

        <Section title="Changes to this notice">
          <p>
            We may update this notice from time to time. The date at the top of this page
            reflects the most recent revision.
          </p>
        </Section>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-14">
      <h2 className="font-heading uppercase text-2xl md:text-3xl text-[#002b5c] mb-6 leading-tight">{title}</h2>
      <div className="space-y-4 text-[#333] leading-[1.85] text-base">
        {children}
      </div>
    </section>
  );
}
